import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, Home } from "lucide-react";
import Logo from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 8;

export default function PaySuccessPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");

  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const [amount, setAmount] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState("paid");
          setAmount(data.amount_total / 100);
          return;
        }
        if (data.status === "expired" || data.payment_status === "expired") {
          setState("failed");
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState("timeout");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        if (attempts >= MAX_ATTEMPTS) {
          setState("timeout");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-grid">
      <div className="absolute inset-0 bg-radial-violet pointer-events-none" />
      <div className="relative w-full max-w-md">
        <div className="flex justify-center mb-6">
          <Logo />
        </div>

        <div
          data-testid="pay-success-card"
          className="gradient-border rounded-2xl p-8 text-center shadow-violet-soft"
        >
          {state === "checking" && (
            <>
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-violet-500/15 border border-violet-500/40">
                <Loader2 className="h-7 w-7 text-violet-300 animate-spin" />
              </div>
              <h1 className="mt-5 font-display text-2xl font-bold">
                Confirmation en cours…
              </h1>
              <p className="mt-2 text-sm text-zinc-400">
                Vérification de votre paiement auprès de Stripe.
              </p>
            </>
          )}

          {state === "paid" && (
            <>
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-500/40">
                <CheckCircle2 className="h-7 w-7 text-emerald-400" />
              </div>
              <h1 className="mt-5 font-display text-2xl font-bold">
                Paiement confirmé !
              </h1>
              {amount && (
                <p
                  data-testid="success-amount"
                  className="mt-3 font-display text-4xl font-extrabold text-violet-300 text-glow-violet"
                >
                  {amount.toFixed(2)}€
                </p>
              )}
              <p className="mt-3 text-sm text-zinc-400">
                Merci pour votre confiance. Un email de confirmation a été
                envoyé à ZFIndustries — vous serez recontacté très vite.
              </p>
            </>
          )}

          {state === "failed" && (
            <>
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-500/15 border border-red-500/40">
                <XCircle className="h-7 w-7 text-red-400" />
              </div>
              <h1 className="mt-5 font-display text-2xl font-bold">
                Paiement échoué
              </h1>
              <p className="mt-2 text-sm text-zinc-400">
                Le paiement n'a pas pu être validé. Vous pouvez réessayer ou
                contacter ZFIndustries.
              </p>
              <button
                onClick={() => navigate(`/paiement/${slug}`)}
                className="mt-6 inline-flex items-center gap-2 rounded-md bg-violet-600 hover:bg-violet-500 px-5 py-2.5 text-sm font-semibold"
              >
                Réessayer
              </button>
            </>
          )}

          {state === "timeout" && (
            <>
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/15 border border-amber-500/40">
                <Loader2 className="h-7 w-7 text-amber-300" />
              </div>
              <h1 className="mt-5 font-display text-2xl font-bold">
                Confirmation différée
              </h1>
              <p className="mt-2 text-sm text-zinc-400">
                Stripe met du temps à confirmer. Si le paiement est passé,
                vous serez recontacté très vite par email.
              </p>
            </>
          )}

          <button
            onClick={() => navigate("/")}
            data-testid="back-home"
            className="mt-6 inline-flex items-center gap-2 rounded-md border border-white/10 bg-white/5 hover:bg-white/10 px-5 py-2.5 text-sm"
          >
            <Home className="h-3.5 w-3.5" /> Retour à l'accueil
          </button>
        </div>
      </div>
    </div>
  );
}
